/**
 * Unit 2.IMPL.F — row click dispatch hook.
 *
 * Bead: insurescope-zkolv.1.2.3.6.
 *
 * Factory hook that returns `(row) => (event) => void`. Owns the three-mode
 * branching that previously lived inline in `DocumentList.tsx` (~80 LOC of
 * JSX-adjacent business logic). DocumentList becomes a thin orchestrator:
 *
 *   const handleRowClick = useRowClickHandler({ mode, navigate, ... });
 *   ...
 *   <TableRow onClick={handleRowClick(row)}>...</TableRow>
 *
 * ## Modes
 *
 *  - `full`   — default library view. Folder rows navigate via react-router
 *               (`navigate(path)`), scope/comparison rows open in a new tab
 *               (`window.open(...)`), document rows call `onDocumentSelect`
 *               to open the side panel.
 *  - `select` — picker mode used by external modals (DocumentPickerModal /
 *               LibrarySelectionModal). Folder rows call `onFolderNavigate`
 *               (the modal walks the tree); non-folder rows toggle the
 *               row's TanStack selection. Mirrors the legacy MUI behavior
 *               where `disableRowSelectionOnClick: false` auto-toggled the
 *               checkbox state on row click.
 *  - `move`   — move-to-folder picker. Only folder rows are interactive;
 *               clicking an allowed folder fires `onFolderSelect(folder)`;
 *               clicking an excluded folder is a no-op.
 *
 * ## Checkbox short-circuit
 * Clicks that originate inside a known interactive descendant (button, link,
 * menu item, checkbox, dropdown trigger) MUST NOT bubble into row-level
 * navigation. The per-row checkbox cell already calls `stopPropagation`, but
 * row-actions menus / inline buttons rely on this guard.
 *
 * ## Portal short-circuit
 * Shadcn/Base UI dialogs, dropdown content, MUI Modal/Dialog, and similar
 * popovers render to a Portal under `document.body`. React synthetic events
 * still bubble through the VDOM tree, so a click on the dialog's title or
 * body reaches the row's `onClick` even though the dialog is outside the
 * row's DOM subtree. Comparing `event.currentTarget.contains(event.target)`
 * uses real DOM ancestry to filter those out without enumerating every
 * portal container.
 *
 * ## Modifier keys (cmd/ctrl/middle-click)
 * The legacy MUI implementation did NOT dispatch on modifier keys — `folder`
 * clicks always navigated same-tab, `scope`/`comparison` always opened a new
 * tab. This hook preserves that contract. Adding cmd-click → new-tab is a
 * new feature, not a "preserve", and is intentionally out of scope here.
 */
import { useCallback } from "react";
import type { NavigateFunction } from "react-router";
import type { Row } from "@tanstack/react-table";

import type { ILibraryListItem } from "@/api";

import type { ILibraryRow } from "../components/documentListColumns.types";

export type RowClickMode = "full" | "select" | "move";

export interface UseRowClickHandlerOptions {
    mode: RowClickMode;
    navigate: NavigateFunction;
    searchParams: URLSearchParams;
    excludeFolderIds?: Set<number>;
    onFolderSelect?: (folder: ILibraryListItem | null) => void;
    onFolderNavigate?: (folderId: number | null) => void;
    onDocumentSelect?: (documentId?: number) => void;
    /** `select`-mode side-effect fired just before folder navigation. */
    markFolderNavigating?: () => void;
}

export type RowClickHandlerFactory = (
    row: Row<ILibraryRow>
) => (event: React.MouseEvent<HTMLTableRowElement>) => void;

// Any descendant that handles its own click MUST NOT trigger row navigation.
const INTERACTIVE_DESCENDANT_SELECTOR =
    'button, a, [role="menuitem"], [role="menu"], [data-slot="checkbox"], [data-slot="dropdown-menu-trigger"]';

export function useRowClickHandler(
    opts: UseRowClickHandlerOptions
): RowClickHandlerFactory {
    const {
        mode,
        navigate,
        searchParams,
        excludeFolderIds,
        onFolderSelect,
        onFolderNavigate,
        onDocumentSelect,
        markFolderNavigating,
    } = opts;

    return useCallback(
        (tanstackRow) => (event) => {
            // EventTarget covers Document / Window / shadow-DOM nodes too; the
            // `as HTMLElement` cast would mask a real bug on synthetic targets.
            const target =
                event.target instanceof HTMLElement ? event.target : null;
            if (!target) {
                return;
            }
            // Portaled descendants (dialogs, dropdowns, MUI Modal, autocomplete
            // popups) bubble through the React tree but live outside this
            // row's DOM. Real DOM ancestry is the source of truth.
            if (event.currentTarget && !event.currentTarget.contains(target)) {
                return;
            }
            if (target.closest(INTERACTIVE_DESCENDANT_SELECTOR)) {
                return;
            }

            const row = tanstackRow.original;

            if (mode === "move") {
                if (
                    row.type === "folder" &&
                    !excludeFolderIds?.has(row._numericId)
                ) {
                    onFolderSelect?.(row._raw);
                }
                return;
            }

            if (mode === "select") {
                if (row.type === "folder") {
                    if (onFolderNavigate) {
                        markFolderNavigating?.();
                        onFolderNavigate(row._numericId);
                    }
                    return;
                }
                // TanStack doesn't ship MUI's `disableRowSelectionOnClick:
                // false` auto-toggle — replicate it explicitly so single-
                // select picker UIs keep working.
                if (tanstackRow.getCanSelect()) {
                    tanstackRow.toggleSelected();
                }
                return;
            }

            // mode === "full"
            // cmd/ctrl-click (or middle-click) on a folder opens it in a
            // new tab — standard browser convention that the legacy MUI
            // build silently swallowed. Brokers reviewing many folders
            // benefit; scope/comparison rows already open in a new tab,
            // so folders join the same convention.
            const newTabIntent =
                event.metaKey || event.ctrlKey || event.button === 1;
            switch (row.type) {
                case "folder": {
                    // Workspace surfaces (the Client Hub Library tab) own
                    // folder navigation as URL STATE (?folder=), never a
                    // document-library route: a supplied onFolderNavigate
                    // takes precedence in full mode so no workspace click
                    // can exit to the library (Unit 7). No main-library
                    // consumer passes it in full mode. Modifier clicks
                    // navigate in place too — a workspace folder has no
                    // library URL to open in a new tab.
                    if (onFolderNavigate) {
                        onFolderNavigate(row._numericId);
                        break;
                    }
                    const isKbFolder = row._raw.is_knowledge_base;
                    const basePath = isKbFolder
                        ? "/organizations/document-library/knowledge-base/folder"
                        : "/organizations/document-library/documents/folder";
                    const search = searchParams.toString();
                    const url = `${basePath}/${row._numericId}${search ? `?${search}` : ""}`;
                    if (newTabIntent) {
                        window.open(url, "_blank");
                        break;
                    }
                    onDocumentSelect?.();
                    navigate(url);
                    break;
                }
                case "scope":
                    window.open(
                        `/organizations/scopes/${row._numericId}`,
                        "_blank"
                    );
                    break;
                case "comparison":
                    window.open(
                        `/organizations/comparisons/${row._numericId}`,
                        "_blank"
                    );
                    break;
                case "document":
                    onDocumentSelect?.(row._numericId);
                    break;
            }
        },
        [
            mode,
            navigate,
            searchParams,
            excludeFolderIds,
            onFolderSelect,
            onFolderNavigate,
            onDocumentSelect,
            markFolderNavigating,
        ]
    );
}
